import { useId } from 'react';
import type { ReactNode } from 'react';
import type { ContractPreset, ContractToken, ThemeContract } from '../../../wire';
import { useT } from '../../../messages';
import { optionLabel } from './slots';

/**
 * **Look** — the preset first, the fine tuning one click deeper (the mockup's
 * rail note: "a preset changes type, color, spacing and edges together … which
 * is why a tenant can never make one section clash with the rest").
 *
 * Everything here renders from the served contract: the presets and their
 * token sets, the token table with its groups, types and closed sets, the
 * motion names. A token the server learns — `button.fill`, "solid / gradient
 * / moving", is the latest — is a control here on the next page load with no
 * change to this file, because a token that arrives with `choices` is drawn
 * as a choice and anything else by its type.
 */

/* ------------------------------------------------------------------ */
/* Tokens, resolved the way the site resolves them                     */
/* ------------------------------------------------------------------ */

/**
 * The preset's tokens with the overrides on top, and the contract's
 * `token_follows` applied: a follower the operator left alone takes its
 * leader's value when the leader was set (the second accent of a business
 * that chose gold is gold). What the colour wells show as "currently".
 */
export function resolveTokens(
  preset: string,
  presets: readonly ContractPreset[],
  overrides: Record<string, string>,
  follows: readonly { follower: string; leader: string }[] = [],
): Record<string, string> {
  const base = presets.find((p) => p.name === preset)?.tokens ?? presets[0]?.tokens ?? {};
  const resolved: Record<string, string> = { ...base };
  const overridden = new Set<string>();
  for (const [name, value] of Object.entries(overrides)) {
    if (value === '') continue;
    resolved[name] = value;
    overridden.add(name);
  }
  for (const { follower, leader } of follows) {
    const value = resolved[leader];
    if (!overridden.has(follower) && overridden.has(leader) && value !== undefined) {
      resolved[follower] = value;
    }
  }
  return resolved;
}

/* ------------------------------------------------------------------ */
/* The swatches                                                        */
/* ------------------------------------------------------------------ */

export function PresetSwatches({
  contract,
  value,
  onPick,
}: {
  contract: ThemeContract;
  value: string;
  onPick: (preset: string) => void;
}): ReactNode {
  const t = useT();
  return (
    <div className="bld-swatches" role="group" aria-label={t('builder.look.presets')}>
      {contract.presets.map((preset) => (
        <button
          key={preset.name}
          type="button"
          className="bld-swatch"
          aria-pressed={preset.name === value}
          title={preset.blurb}
          onClick={() => onPick(preset.name)}
        >
          <i
            aria-hidden="true"
            style={{
              background: `linear-gradient(90deg, ${preset.tokens['color.paper'] ?? '#fff'} 50%, ${preset.tokens['color.accent'] ?? '#888'} 50%)`,
              borderRadius: preset.tokens['radius.md'],
            }}
          />
          <span style={{ fontFamily: preset.tokens['font.display'] }}>
            {t('builder.presetName', { preset: preset.name }) || optionLabel(t, preset.name)}
          </span>
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The drawer's contents                                               */
/* ------------------------------------------------------------------ */

export function LookControls({
  contract,
  preset,
  tokens,
  motion,
  siteTitle,
  tagline,
  fallbackName,
  onPreset,
  onTokens,
  onMotion,
  onSiteTitle,
  onTagline,
}: {
  contract: ThemeContract;
  preset: string;
  tokens: Record<string, string>;
  motion: string;
  siteTitle: string;
  tagline: string;
  fallbackName: string;
  onPreset: (preset: string) => void;
  onTokens: (tokens: Record<string, string>) => void;
  onMotion: (motion: string) => void;
  onSiteTitle: (value: string) => void;
  onTagline: (value: string) => void;
}): ReactNode {
  const t = useT();
  const titleId = useId();
  const taglineId = useId();
  const base = contract.presets.find((p) => p.name === preset)?.tokens ?? {};
  const resolved = resolveTokens(preset, contract.presets, tokens, contract.token_follows);

  const set = (name: string, value: string): void => {
    const next = { ...tokens };
    if (value === '' || value === base[name]) delete next[name];
    else next[name] = value;
    onTokens(next);
  };

  return (
    <div className="bld-stack">
      <section className="bld-ins-group">
        <h4 className="bld-label">{t('builder.look.presets')}</h4>
        <PresetSwatches contract={contract} value={preset} onPick={onPreset} />
        <p className="bld-hint">{contract.presets.find((p) => p.name === preset)?.blurb ?? ''}</p>
      </section>

      <section className="bld-ins-group">
        <h4 className="bld-label">{t('builder.look.words')}</h4>
        <div className="bld-field">
          <label htmlFor={titleId}>{t('branding.siteTitle')}</label>
          <input
            id={titleId}
            className="bld-input"
            value={siteTitle}
            placeholder={fallbackName}
            onChange={(e) => onSiteTitle(e.target.value)}
          />
          <span className="bld-hint">{t('builder.look.siteTitleHint')}</span>
        </div>
        <div className="bld-field">
          <label htmlFor={taglineId}>{t('branding.tagline')}</label>
          <input
            id={taglineId}
            className="bld-input"
            value={tagline}
            onChange={(e) => onTagline(e.target.value)}
          />
        </div>
      </section>

      {contract.token_groups.map((group) => {
        const own = contract.tokens.filter((token) => token.group === group.key);
        if (own.length === 0) return null;
        const changed = own.filter((token) => tokens[token.name] !== undefined).length;
        return (
          <details key={group.key} className="bld-more" open={group.key === 'color'}>
            <summary>
              {t('builder.look.group', { group: group.key }) || group.label}
              {changed === 0 ? null : (
                <span className="bld-mark">{t('branding.tokensChanged', { count: changed })}</span>
              )}
            </summary>
            <div className="bld-stack">
              {own.map((token) => (
                <TokenField
                  key={token.name}
                  token={token}
                  preset={base[token.name] ?? ''}
                  effective={resolved[token.name] ?? ''}
                  value={tokens[token.name] ?? ''}
                  onChange={(value) => set(token.name, value)}
                />
              ))}
            </div>
          </details>
        );
      })}

      {/* The staff app's look moved to Setup (B8); one line says where. */}
      <p className="bld-hint">{t('look.movedFromEditor')}</p>

      {contract.motion.options.length === 0 ? null : (
        <fieldset className="bld-list">
          <legend>{t('branding.motion')}</legend>
          {contract.motion.options.map((option) => (
            <label className="bld-check" key={option.name}>
              <input
                type="radio"
                name="bld-motion"
                aria-label={option.name}
                checked={motion === option.name}
                onChange={() => onMotion(option.name)}
              />
              <span>
                <strong>{t('builder.motionName', { motion: option.name }) || option.name}</strong>{' '}
                <span className="bld-hint">{option.blurb}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
    </div>
  );
}

/**
 * One token's control, chosen by what the server sent: a closed set as a row
 * of choices (the preset's own marked), a colour as a well beside its text box
 * (the contract admits `rgb()`, `oklch()`… and a native well can only make
 * hex, so the box stays the real control), anything else as a box.
 */
function TokenField({
  token,
  preset,
  effective,
  value,
  onChange,
}: {
  token: ContractToken;
  preset: string;
  effective: string;
  value: string;
  onChange: (value: string) => void;
}): ReactNode {
  const t = useT();
  const id = useId();
  if (token.choices.length > 0) {
    const current = value === '' ? preset : value;
    return (
      <div className="bld-field">
        <span id={id}>{token.label}</span>
        <div className="bld-choice" role="group" aria-labelledby={id}>
          {token.choices.map((choice) => (
            <button
              key={choice}
              type="button"
              aria-pressed={current === choice}
              onClick={() => onChange(choice === preset ? '' : choice)}
            >
              {optionLabel(t, choice)}
            </button>
          ))}
        </div>
        <span className="bld-hint">
          {t('builder.look.presetSays', { value: optionLabel(t, preset === '' ? '—' : preset) })}
        </span>
      </div>
    );
  }
  return (
    <div className="bld-field">
      <label htmlFor={id}>{token.label}</label>
      <span className="bld-token">
        {token.type === 'color' ? (
          <input
            type="color"
            aria-label={t('branding.colourPicker', { label: token.label })}
            value={/^#[0-9a-f]{6}$/i.test(effective) ? effective : '#000000'}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : null}
        <input
          id={id}
          className="bld-input"
          type={token.type === 'number' ? 'number' : 'text'}
          value={value}
          placeholder={preset}
          onChange={(e) => onChange(e.target.value)}
        />
      </span>
      <span className="bld-hint">{token.hint}</span>
    </div>
  );
}
